/**
 * Target frame — a WoW-style "selected target" panel pinned to the top of the screen, in chunky
 * pixel-art style: a portrait, the target's name, an optional health bar, and an ACTION HINT made
 * of a pixel keycap + a verb (e.g. [T] Talk for NPCs, [P] Inspect for players). Shown while a
 * target is selected (left-click), hidden when deselected.
 */
import { drawKeyCap } from "../three/npc.js";

const KS = 18; // keycap canvas resolution

let root: HTMLDivElement | null = null;
let portrait: HTMLDivElement | null = null;
let nameEl: HTMLDivElement | null = null;
let track: HTMLDivElement | null = null;
let barFill: HTMLDivElement | null = null;
let hpText: HTMLDivElement | null = null;
let hintRow: HTMLDivElement | null = null;

function mount(): void {
  if (root) return;
  root = document.createElement("div");
  root.style.cssText =
    "position:fixed;top:14px;left:50%;transform:translateX(-50%);z-index:12;display:none;" +
    "align-items:center;gap:10px;padding:8px 14px 8px 8px;" +
    "background:var(--gl-panel);border:1px solid var(--gl-border);border-radius:10px;" +
    "box-shadow:0 12px 36px rgba(0,0,0,.6);font-family:var(--gl-font);color:var(--gl-text);";

  portrait = document.createElement("div");
  portrait.style.cssText =
    "flex:0 0 auto;width:50px;height:50px;background:var(--gl-panel-2);border:1px solid var(--gl-border);border-radius:8px;" +
    "display:flex;align-items:center;justify-content:center;overflow:hidden;image-rendering:pixelated;";

  const right = document.createElement("div");
  right.style.cssText = "display:flex;flex-direction:column;gap:6px;min-width:150px;";
  nameEl = document.createElement("div");
  nameEl.style.cssText = "font-size:11px;letter-spacing:.16em;color:var(--gl-text);text-transform:uppercase;";

  track = document.createElement("div");
  track.style.cssText =
    "position:relative;height:8px;border-radius:4px;overflow:hidden;background:#1c1c1c;border:1px solid var(--gl-border);";
  barFill = document.createElement("div");
  barFill.style.cssText = "position:absolute;left:0;top:0;bottom:0;width:100%;background:#8a8a8a;";
  hpText = document.createElement("div");
  hpText.style.cssText =
    "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:8px;color:#fff;font-weight:700;";
  track.append(barFill, hpText);

  // action hints: one or more pixel keycaps + the verb each triggers
  hintRow = document.createElement("div");
  hintRow.style.cssText = "display:flex;align-items:center;flex-wrap:wrap;gap:5px 12px;";

  right.append(nameEl, track, hintRow);
  root.append(portrait, right);
  document.body.appendChild(root);
}

/** One key → verb hint shown on the target card. */
export interface TargetAction { key: string; action: string; }

/** Show the target card. `hp`/`maxHp` omitted ⇒ no health bar (e.g. players). `actions` is one or
 *  more key hints (e.g. [P] Inspect, [B] Trade). */
export function showTarget(head: HTMLCanvasElement | null, name: string, opts: { actions: TargetAction[]; hp?: number; maxHp?: number }): void {
  mount();
  if (portrait) {
    portrait.replaceChildren();
    if (head) {
      const c = document.createElement("canvas");
      c.width = head.width; c.height = head.height;
      c.getContext("2d")!.drawImage(head, 0, 0);
      c.style.cssText = "width:48px;height:48px;image-rendering:pixelated;";
      portrait.appendChild(c);
    }
  }
  if (nameEl) nameEl.textContent = name;
  if (track) {
    if (opts.hp != null && opts.maxHp != null) { track.style.display = "block"; setNpcTargetHealth(opts.hp, opts.maxHp); }
    else track.style.display = "none";
  }
  if (hintRow) {
    hintRow.replaceChildren(...opts.actions.map(({ key, action }) => {
      const g = document.createElement("div"); g.style.cssText = "display:flex;align-items:center;gap:6px;";
      const cap = document.createElement("canvas"); cap.width = KS; cap.height = KS;
      cap.style.cssText = "width:17px;height:17px;image-rendering:pixelated;";
      const ctx = cap.getContext("2d")!; ctx.imageSmoothingEnabled = false; drawKeyCap(ctx, 0, 0, KS, key);
      const lbl = document.createElement("span");
      lbl.style.cssText = "font-size:9px;letter-spacing:.1em;color:var(--gl-muted);text-transform:uppercase;";
      lbl.textContent = action;
      g.append(cap, lbl);
      return g;
    }));
  }
  if (root) root.style.display = "flex";
}

/** NPC convenience: a "Talk" target with a health bar, on the T key. */
export function showNpcTarget(head: HTMLCanvasElement | null, name: string, hp: number, maxHp: number): void {
  showTarget(head, name, { actions: [{ key: "T", action: "Talk" }], hp, maxHp });
}

export function setNpcTargetHealth(hp: number, maxHp: number): void {
  const pct = maxHp > 0 ? Math.max(0, Math.min(1, hp / maxHp)) : 0;
  if (barFill) barFill.style.width = `${(pct * 100).toFixed(0)}%`;
  if (hpText) hpText.textContent = `${Math.round(hp)} / ${Math.round(maxHp)}`;
}

export function hideNpcTarget(): void {
  if (root) root.style.display = "none";
}

export function destroyNpcTarget(): void {
  root?.remove();
  root = portrait = nameEl = track = barFill = hpText = hintRow = null;
}
